import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerContext,
  CustomerReadiness,
  CustomerStatus,
  CustomerSummary,
  CustomerTimelineEntry,
  DuplicateCheckResult,
  PaginatedResult,
  ProfileCompleteness,
} from '@optiflow/types';
import { api } from '@/services/apiClient';

export interface CustomerListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: CustomerStatus | '';
}

export interface CustomerPayload {
  fullName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  mobile?: string;
  alternateMobile?: string;
  email?: string;
  dateOfBirth?: string;
  gender?: string;
  preferredLanguage?: string;
  notes?: string;
}

export interface DuplicateCheckPayload {
  mobile?: string;
  email?: string;
  fullName?: string;
  excludeCustomerId?: string;
}

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (params: CustomerListParams) => [...customerKeys.lists(), params] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
  completeness: (id: string) => [...customerKeys.all, 'completeness', id] as const,
  readiness: (id: string, context: string) =>
    [...customerKeys.all, 'readiness', id, context] as const,
  timeline: (id: string) => [...customerKeys.all, 'timeline', id] as const,
};

/** Strips empty values so the API never receives `status=` or `search=`. */
function toParams(params: CustomerListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  };
  if (params.search?.trim()) out.search = params.search.trim();
  if (params.status) out.status = params.status;
  return out;
}

export function useCustomersQuery(params: CustomerListParams) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<CustomerSummary>>('/customers', { params: toParams(params) }),
    // Keeps the previous page visible while the next loads, so the table does not flash empty.
    placeholderData: (previous) => previous,
  });
}

export function useCustomerQuery(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(id ?? ''),
    queryFn: () => api.get<CustomerSummary>(`/customers/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useCustomerCompletenessQuery(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.completeness(id ?? ''),
    queryFn: () => api.get<ProfileCompleteness>(`/customers/${id ?? ''}/profile-completeness`),
    enabled: Boolean(id),
  });
}

export function useCustomerReadinessQuery(id: string | undefined, context: CustomerContext) {
  return useQuery({
    queryKey: customerKeys.readiness(id ?? '', context),
    queryFn: () =>
      api.get<CustomerReadiness>(`/customers/${id ?? ''}/readiness`, { params: { context } }),
    enabled: Boolean(id),
  });
}

export function useCustomerTimelineQuery(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.timeline(id ?? ''),
    queryFn: () => api.get<CustomerTimelineEntry[]>(`/customers/${id ?? ''}/timeline`),
    enabled: Boolean(id),
  });
}

export function useCreateCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomerPayload) => api.post<CustomerSummary>('/customers', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

export function useUpdateCustomerMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomerPayload) =>
      api.patch<CustomerSummary>(`/customers/${id}`, payload),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(id), customer);
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerKeys.completeness(id) });
      void queryClient.invalidateQueries({ queryKey: customerKeys.timeline(id) });
    },
  });
}

export function useChangeCustomerStatusMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { status: CustomerStatus; reason?: string }) =>
      api.patch<CustomerSummary>(`/customers/${id}/status`, payload),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(id), customer);
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerKeys.timeline(id) });
    },
  });
}

/** Not a query: the caller decides when to probe, typically as the mobile field is completed. */
export function useCheckDuplicatesMutation() {
  return useMutation({
    mutationFn: (payload: DuplicateCheckPayload) =>
      api.post<DuplicateCheckResult>('/customers/check-duplicates', payload),
  });
}
